/* Welcome subscription offer (sections/nuitae-popup.liquid).
   Opens once per visitor, after a delay or a scroll, or when the mouse leaves the page on desktop.
   Visitors from Meta or TikTok ads (fbclid, ttclid, or a Meta or TikTok utm_source) see it sooner
   and are tagged source-paid-social. It never opens over another dialog, never right after one
   closes, and never again after a sign-up; after it's closed it waits a number of days.
   It submits through Shopify's customer form in the background, and falls back to a normal
   form post (which handles Shopify's bot check) if the background post doesn't come back clean.
   The dialog itself (Esc, backdrop click, focus trap, focus return) is handled in nuitae.js. */
(() => {
  const d = document;
  const dialog = d.querySelector('[data-n-popup]');
  if (!dialog) return;

  const local = {
    get(key) { try { return window.localStorage.getItem(key); } catch (err) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (err) { /* private mode */ } },
  };
  const session = {
    get(key) { try { return window.sessionStorage.getItem(key); } catch (err) { return null; } },
    set(key, value) { try { window.sessionStorage.setItem(key, value); } catch (err) { /* private mode */ } },
    remove(key) { try { window.sessionStorage.removeItem(key); } catch (err) { /* private mode */ } },
  };

  const DONE = 'nuitae-popup-done';
  const SNOOZE = 'nuitae-popup-snooze';
  const SENT = 'nuitae-popup-sent';
  const PAID = 'nuitae-paid-social';
  const DAY = 86400000;
  const started = Date.now();
  const preview = dialog.dataset.preview === 'true';

  const form = dialog.querySelector('[data-n-popup-form]');
  const ask = dialog.querySelector('.n-popup__ask');
  const done = dialog.querySelector('[data-n-popup-done]');
  const error = dialog.querySelector('[data-n-popup-error]');
  const email = form ? form.querySelector('input[type="email"]') : null;

  /* Paid social: fbclid or ttclid on the landing URL, or a Meta or TikTok utm_source. Kept for the
     session (for timing) and 30 days (for the tag). */
  const params = new URLSearchParams(window.location.search);
  const source = (params.get('utm_source') || '').toLowerCase();
  const fromAds = params.has('fbclid') || params.has('ttclid')
    || /^(facebook|fb|instagram|ig|meta|an|msg|messenger|tiktok)/.test(source);
  if (fromAds) {
    session.set(PAID, '1');
    local.set(PAID, String(started));
  }
  const social = session.get(PAID) === '1';
  const paidRecently = social || started - Number(local.get(PAID) || 0) < 30 * DAY;

  const isDone = () => local.get(DONE) === '1';
  const markDone = () => local.set(DONE, '1');

  function addPaidTag() {
    const tags = form && form.querySelector('[data-n-popup-tags]');
    if (paidRecently && tags && !tags.value.includes('source-paid-social')) tags.value += ',source-paid-social';
  }

  function open() {
    if (dialog.open) return;
    dialog._trigger = d.activeElement;
    dialog.showModal();
    d.documentElement.classList.add('n-lock');
    /* Start on the words, not the close button, so no focus ring flashes on open; Tab reaches the field next. */
    const start = done && !done.hidden ? done : ask;
    if (start) start.focus({ preventScroll: true });
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
  }

  function close() {
    const closer = dialog.querySelector('[data-n-close]');
    if (dialog.open && closer) closer.click();
  }

  function showDone(address) {
    markDone();
    if (ask) ask.hidden = true;
    if (done) {
      done.hidden = false;
      dialog.setAttribute('aria-labelledby', 'n-popup-done-title');
      done.focus({ preventScroll: true });
    }
    if (address) {
      try { if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email: address }); } catch (err) { /* optional */ }
    }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('nuitae_email_signup', { source: 'popup', paid_social: paidRecently });
      }
    } catch (err) { /* optional */ }
    d.dispatchEvent(new CustomEvent('nuitae:signup', { detail: { source: 'popup', paidSocial: paidRecently } }));
  }

  /* Closing without joining snoozes it. */
  dialog.addEventListener('close', () => {
    if (preview || isDone()) return;
    const days = Number(dialog.dataset.snooze) || 7;
    local.set(SNOOZE, String(Date.now() + days * DAY));
  });

  /* Background submit, with a normal post as the fallback. */
  if (form) {
    form.addEventListener('submit', async (event) => {
      addPaidTag();
      if (!window.fetch || !window.DOMParser) {
        session.set(SENT, '1');
        return;
      }
      event.preventDefault();
      if (email && !email.checkValidity()) {
        email.reportValidity();
        return;
      }
      const button = form.querySelector('[type="submit"]');
      if (button && button.disabled) return;
      if (button) { button.disabled = true; button.classList.add('is-loading'); }
      if (error) error.hidden = true;
      if (email) {
        email.removeAttribute('aria-invalid');
        email.setAttribute('aria-describedby', 'n-popup-terms');
      }
      try {
        const response = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin', headers: { Accept: 'text/html' } });
        const url = response.url || '';
        if (url.includes('customer_posted=true')) {
          showDone(email && email.value);
          return;
        }
        if (url.includes('/challenge')) throw new Error('challenge');
        if (!response.ok) throw new Error('http');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const message = page.querySelector('#n-popup-form-error:not([hidden])');
        if (message && message.textContent.trim() && error && email) {
          error.textContent = message.textContent.trim();
          error.hidden = false;
          email.setAttribute('aria-invalid', 'true');
          email.setAttribute('aria-describedby', error.id);
          email.focus();
          return;
        }
        if (page.querySelector('[data-n-popup-done]:not([hidden])')) {
          showDone(email && email.value);
          return;
        }
        throw new Error('unclear');
      } catch (err) {
        session.set(SENT, '1');
        HTMLFormElement.prototype.submit.call(form);
      } finally {
        if (button) { button.disabled = false; button.classList.remove('is-loading'); }
      }
    });
  }

  /* Copy the code. */
  const copy = dialog.querySelector('[data-n-popup-copy]');
  const copyStatus = dialog.querySelector('[data-n-popup-copy-status]');
  const copyLabel = copy ? copy.textContent : '';
  let copyTimer;
  if (copy) {
    copy.addEventListener('click', async () => {
      const code = dialog.querySelector('[data-n-popup-code]');
      const text = code ? code.textContent.trim() : '';
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        const range = d.createRange();
        range.selectNodeContents(code);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        if (copyStatus) copyStatus.textContent = copy.dataset.copyError;
        return;
      }
      copy.textContent = copy.dataset.copied || 'Copied';
      if (copyStatus) copyStatus.textContent = copy.dataset.copied;
      clearTimeout(copyTimer);
      copyTimer = setTimeout(() => { copy.textContent = copyLabel; }, 2000);
    });
  }

  /* Theme editor: open while the section is selected, never on its own. */
  if (preview) {
    d.addEventListener('shopify:section:select', (event) => { if (event.target.contains(dialog)) open(); });
    d.addEventListener('shopify:section:deselect', (event) => { if (event.target.contains(dialog)) close(); });
    return;
  }

  /* A plain welcome link lets visitors reopen their offer without applying it. */
  if (params.get('welcome') === '1') {
    addPaidTag();
    open();
    return;
  }

  /* Coming back from a normal post of this form: show the result. */
  if (session.get(SENT) === '1') {
    session.remove(SENT);
    if (done && !done.hidden) {
      open();
      showDone();
      return;
    }
    if (error && !error.hidden) {
      open();
      return;
    }
  }
  /* Someone who just joined through another form on this page is done too. */
  if (done && !done.hidden) {
    markDone();
    return;
  }
  d.addEventListener('submit', (event) => {
    const other = event.target;
    if (other !== form && other.matches('form') && other.querySelector('input[name="form_type"][value="customer"]')) markDone();
  }, true);

  if (isDone() || Date.now() < Number(local.get(SNOOZE) || 0)) return;

  /* When to open. */
  const delay = (Number(social ? dialog.dataset.socialDelay : dialog.dataset.delay) || 20) * 1000;
  const scrollAt = Number(social ? dialog.dataset.socialScroll : dialog.dataset.scroll) || 50;
  const earliest = Math.min(delay, 3000);
  let lastClosed = 0;
  let fired = false;
  let timer = 0;

  d.addEventListener('close', (event) => {
    if (event.target !== dialog && event.target instanceof HTMLDialogElement) lastClosed = Date.now();
  }, true);

  function busy() {
    if (window.matchMedia('(max-width: 749px)').matches) {
      if (d.querySelector('[data-n-sticky].is-visible')) return true;
      const buy = d.querySelector('.d-pdp__info');
      if (buy) {
        const rect = buy.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) return true;
      }
    }
    if (d.querySelector('dialog[open]')) return true;
    if (Date.now() - lastClosed < 8000) return true;
    const active = d.activeElement;
    return !!(active && (active.matches('input, textarea, select') || active.isContentEditable));
  }

  function stop() {
    clearTimeout(timer);
    window.removeEventListener('scroll', onScroll);
    d.removeEventListener('mouseout', onExit);
  }

  function fire(reason) {
    if (fired || isDone()) return;
    if (d.hidden || busy()) {
      clearTimeout(timer);
      timer = setTimeout(() => fire(reason), 2000);
      return;
    }
    fired = true;
    stop();
    dialog.dataset.openedBy = reason || 'timer';
    dialog.dataset.openedAt = String(Math.round(window.performance ? performance.now() : 0));
    local.set(SNOOZE, String(Date.now() + 7 * DAY));
    open();
  }

  function onScroll() {
    if (Date.now() - started < earliest) return;
    const room = d.documentElement.scrollHeight - window.innerHeight;
    if (room > 0 && (window.scrollY / room) * 100 >= scrollAt) fire('scroll');
  }

  function onExit(event) {
    if (!event.relatedTarget && event.clientY <= 0 && Date.now() - started > 3000) fire('exit');
  }

  timer = setTimeout(() => fire('timer'), delay);

  if (dialog.dataset.exit === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    d.addEventListener('mouseout', onExit);
  }
})();
